import { DateField, EnumField } from 'framework/ui/field';
import { DataGridLayout, List, ListPageLayout, TableBodyWithGrouping } from 'framework/list';
import { Box, CircularProgress, Link, TableRow, Typography } from '@mui/material';
import { VendorDto } from '../types';
import { useAuthorization } from 'framework/authorization';
import { AgreementPermissionsProvider, useAgreementPermissions } from '../shared/buttons/AgreementPermissionsContext';
import useVendorService from '../useVendorService';
import { useVendorId } from '../routes';
import { LEGAL_ENTITY } from 'framework/form';
import { AGREEMENT_TYPES_VALUES, VENDOR_DURACTION_TYPE_VALUES } from '../constants';
import AgreementStatusField from '../shared/fields/AgreementStatusField';
import CreateAgreementButton from '../shared/buttons/CreateAgreementButton';
import FinalizeAgreementButton from '../shared/buttons/FinalizeAgreementButton';
import TerminateAgreementButton from '../shared/buttons/TerminateAgreementButton';
import EditAgreementButton from '../shared/buttons/EditAgreementButton';
import { useShowContext } from 'framework/show';
import { formatUrl, isUrl } from 'framework/utils';
import { DocumentStorageLoadListField } from 'domain/shared/artifact/field';

const COUNT_TABLE_TD = 7;

const AgreementDitails = () => {
  const { data } = useShowContext();
  const publicOfferLink = data.getValueBySource('publicOfferLink');
  const signedContractAttachment = data.getValueBySource('signedContractAttachment');

  return (
    <Box p="5px">
      {publicOfferLink &&
        (isUrl(publicOfferLink) ? (
          <Typography>
            Public offer link:
            <Link target="_blank" rel="noopener noreferrer" href={formatUrl(publicOfferLink)}>
              {publicOfferLink}
            </Link>
          </Typography>
        ) : (
          <Typography>Public offer link: {publicOfferLink}</Typography>
        ))}

      {signedContractAttachment && (
        <DocumentStorageLoadListField label="Signed contract" source="signedContractAttachment" />
      )}
    </Box>
  );
};

const AgreementActions = ({ vendorData }: { vendorData: VendorDto }) => {
  const { isLoading } = useAgreementPermissions();

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" paddingY={0.5}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" columnGap={0.5}>
      <FinalizeAgreementButton vendorData={vendorData} />
      <TerminateAgreementButton vendorData={vendorData} />
      <EditAgreementButton vendorData={vendorData} />
    </Box>
  );
};

const AgreementsVendor = () => {
  const id = useVendorId()!;
  const { getVendorAgreements } = useVendorService();
  const { data: vendorData } = useShowContext();
  const { isGranted } = useAuthorization();

  return (
    <List getDataMethod={loadParams => getVendorAgreements(id, loadParams)}>
      <ListPageLayout
        actions={[
          <Box key="create-agreement-box" sx={{ marginLeft: 'auto' }} mt={1}>
            <CreateAgreementButton key="create-agreement" isVisible={isGranted('CREATE_AGREEMENT')} />
          </Box>,
        ]}
      >
        <DataGridLayout
          isSmallTable
          buttons={[
            <AgreementPermissionsProvider key="agreement-actions">
              <AgreementActions vendorData={vendorData as unknown as VendorDto} />
            </AgreementPermissionsProvider>,
          ]}
          tableBodyComponent={(props: any) => (
            <TableBodyWithGrouping
              {...props}
              childrenSource="publicOfferLink"
              toggleSource="legalEntity"
              isRequiredChildrenSource={false}
              CustomChildTemplate={
                <TableRow
                  sx={{
                    '&:hover': { backgroundColor: 'transparent !important' },
                  }}
                >
                  <td colSpan={COUNT_TABLE_TD}>
                    <AgreementDitails />
                  </td>
                </TableRow>
              }
            />
          )}
        >
          <EnumField label="Legal entity" source="legalEntity" options={LEGAL_ENTITY} width="25%" />
          <AgreementStatusField source="status" />
          <EnumField label="Agreement type" source="agreementType" options={AGREEMENT_TYPES_VALUES} />
          <EnumField label="Duration type" source="durationType" options={VENDOR_DURACTION_TYPE_VALUES} />
          <DateField label="Start date" source="startDate" />
          <DateField label="End date" source="endDate" />
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default AgreementsVendor;
